import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Logo } from "@/components/Logo";

/**
 * Auth gate. While Firebase is still resolving the initial auth state we
 * render nothing — bouncing to /login during that race would log signed-in
 * users out on hard refresh.
 *
 * v0.1: signed-in users without a verified email reach the app anyway; the
 * email-verify gate is added later in v0.2 once partner-invite emails are wired.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { initialized, isSignedIn } = useAuth();
  const location = useLocation();

  if (!initialized) {
    return (
      <div className="app-boot-loading" aria-busy="true" aria-label="Wczytuję...">
        <Logo size={32} textSize={20} />
        <div className="app-boot-loading__card">
          <div className="skeleton-line app-boot-loading__line--sm" />
          <div className="skeleton-line app-boot-loading__line--lg" />
          <div className="skeleton-line app-boot-loading__line--md" />
          <div className="skeleton-line" />
          <div className="skeleton-line" style={{ width: "85%" }} />
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}
